"use client";

import Image from "next/image";
import { ArrowUp, Check, Globe, Paperclip, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { AeoSubpageCopy } from "./aeo-page-copy";

type Hero = AeoSubpageCopy["hero"];
type Phase = "typing" | "thinking" | "answering" | "done";

/**
 * An answer engine window (layer9.com's typed prompt): the question types
 * itself, the engine "searches", then the answer streams in word by word
 * with the example business highlighted and cited as a source. Cycles
 * through the demos; reduced motion shows the first one complete.
 */
export function CcAeoAnswerWindow({ hero, animate = true }: { hero: Hero; animate?: boolean }) {
  const reduced = useCcPrefersReducedMotion();
  const rootRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [demoIndex, setDemoIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>("typing");
  const [chars, setChars] = useState(0);
  const [words, setWords] = useState(0);

  const demo = hero.demos[demoIndex];
  const engine = hero.engines[demo.engine] ?? hero.engines[0];
  const answerWords = `${demo.answerBefore}\u0000${demo.answerAfter}`.split(
    " ",
  );

  // Only animate while the window is on screen.
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (reduced || !visible || !animate) return;
    let timer = 0;
    if (phase === "typing") {
      if (chars < demo.question.length)
        timer = window.setTimeout(() => setChars((c) => c + 1), 34);
      else timer = window.setTimeout(() => setPhase("thinking"), 380);
    } else if (phase === "thinking") {
      timer = window.setTimeout(() => setPhase("answering"), 1100);
    } else if (phase === "answering") {
      if (words < answerWords.length)
        timer = window.setTimeout(() => setWords((w) => w + 1), 70);
      else timer = window.setTimeout(() => setPhase("done"), 200);
    } else {
      timer = window.setTimeout(() => {
        setDemoIndex((i) => (i + 1) % hero.demos.length);
        setChars(0);
        setWords(0);
        setPhase("typing");
      }, 4200);
    }
    return () => window.clearTimeout(timer);
  }, [
    phase,
    chars,
    words,
    reduced,
    visible,
    demo.question.length,
    answerWords.length,
    hero.demos.length,
    animate,
  ]);

  // Reduced motion: the first demo, complete and still.
  const view = reduced || !animate
    ? {
        phase: "done" as Phase,
        chars: demo.question.length,
        words: answerWords.length,
      }
    : { phase, chars, words };

  // Rebuild the streamed answer, with the brand (marked by \u0000) highlighted.
  const shown = answerWords.slice(0, view.words).join(" ");
  const [before, after] = shown.split("\u0000");
  const brandShown = shown.includes("\u0000");

  return (
    <div ref={rootRef} className="cc-aeo-window" aria-hidden="true">
      <div className="cc-aeo-window__engines">
        {hero.engines.map((engine, index) => (
          <span
            key={engine.id}
            data-on={index === demo.engine ? "true" : undefined}
          >
            <Image
              src={engine.logo}
              alt=""
              width={14}
              height={14}
              unoptimized
              loading="eager"
            />
            {engine.name}
          </span>
        ))}
      </div>

      <div className="cc-aeo-window__thread">
        <p className="cc-aeo-window__question">
          {demo.question.slice(0, view.chars)}
          {view.phase === "typing" ? (
            <i className="cc-aeo-window__caret" />
          ) : null}
        </p>

        {view.phase !== "typing" ? (
          <div className="cc-aeo-window__answer">
            <span className="cc-aeo-window__avatar">
              <Image
                src={engine.logo}
                alt=""
                width={16}
                height={16}
                unoptimized
                loading="eager"
              />
            </span>
            <div>
              {view.phase === "thinking" ? (
                <p className="cc-aeo-window__thinking">
                  <Search size={13} strokeWidth={2.2} />
                  {hero.window.sourcesLabel} …
                </p>
              ) : (
                <p className="cc-aeo-window__text">
                  {before}
                  {brandShown ? <mark>{demo.brand}</mark> : null}
                  {after}
                </p>
              )}
              {view.phase === "done" ? (
                <span className="cc-aeo-window__source">
                  <Globe size={13} strokeWidth={2.2} />
                  {demo.source}
                  <em>
                    <Check size={11} strokeWidth={3} />
                    {hero.window.citedLabel}
                  </em>
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="cc-aeo-window__input">
        <Paperclip size={15} strokeWidth={2} />
        <span>{hero.window.placeholder}</span>
        <i>
          <ArrowUp size={14} strokeWidth={2.6} />
        </i>
      </div>
    </div>
  );
}
